import React, { useEffect, useState } from 'react';
import { CalendarRange } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const TYPES = [
  ['annual', 'Annual'], ['sick', 'Sick'], ['emergency', 'Emergency'], ['public_holiday', 'Public Holiday'],
  ['unpaid', 'Unpaid'], ['maternity', 'Maternity'], ['paternity', 'Paternity'],
];

// Rule: which leave types may be applied as separate (non-consecutive) dates.
export const MultiDateSettingsCard = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/api/leaves/multi-date-settings').then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const toggleType = (t) => setS({ ...s, excluded_types: s.excluded_types.includes(t) ? s.excluded_types.filter((x) => x !== t) : [...s.excluded_types, t] });
  const save = async () => {
    setSaving(true);
    try { const r = await api.put('/api/leaves/multi-date-settings', s); setS(r.data); toast({ title: 'Saved', description: 'Separate-dates leave rules updated' }); }
    catch (e) { toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    setSaving(false);
  };
  return (
    <Card data-testid="multi-date-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><CalendarRange className="h-4 w-4 text-sky-600" />Separate-dates leave</CardTitle>
        <CardDescription>Staff can pick several non-consecutive days (e.g. every Friday) and send them as one approval request.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-center gap-3 text-sm"><Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="md-enabled-switch" />Allow separate-dates requests</label>
        <div>
          <p className="text-xs text-gray-500 mb-2">Leave types allowed (untick to exclude)</p>
          <div className="flex flex-wrap gap-2">
            {TYPES.map(([v, l]) => {
              const on = !s.excluded_types.includes(v);
              return (
                <button key={v} type="button" disabled={!s.enabled} onClick={() => toggleType(v)} data-testid={`md-type-${v}`}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors disabled:opacity-50 ${on ? 'bg-sky-50 border-sky-300 text-sky-800 font-medium' : 'bg-white text-gray-400 line-through'}`}>{l}</button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">Dates within next <Input type="number" min={1} max={365} className="h-8 w-20" value={s.max_days_ahead} onChange={(e) => setS({ ...s, max_days_ahead: Number(e.target.value) })} data-testid="md-max-days-ahead" /> days</label>
          <label className="flex items-center gap-2">Max <Input type="number" min={2} max={120} className="h-8 w-20" value={s.max_dates} onChange={(e) => setS({ ...s, max_dates: Number(e.target.value) })} data-testid="md-max-dates" /> dates per request</label>
        </div>
        <Button size="sm" onClick={save} disabled={saving} data-testid="md-settings-save">{saving ? 'Saving…' : 'Save rules'}</Button>
      </CardContent>
    </Card>
  );
};
